import React, { useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';

import { ManageGalleryModal } from 'src/components/favorites/ManageGalleryModal';
import NoFavoritesEmptyState from 'src/components/favorites/NoFavoritesEmptyState';
import { GalleryPreviewRow } from 'src/components/favorites/GalleryPreview';
import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import Loader from 'src/components/ui/Loader';
import { colours } from 'src/constants/colours';
import useAlerts from 'src/hooks/useAlerts';
import type { ModalProps } from 'src/hooks/useModalOpenState';

import { useAddContentToGalleryMutation } from 'src/hooks/__generated__/queries';
import { AnalyticsEvent } from 'src/utils/analytics';
import useApolloCacheUpdate from 'src/hooks/useApolloCacheUpdate';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent, styledTag } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { useGalleries } from 'src/hooks/useGalleries';
import { GalleryAccessPolicy } from 'src/types/__generated__/graphql';
import { galleriesQueries } from 'src/constants/galleries';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        height: 35rem;
    }
    .MuiDialogContent-root {
        padding: 0 1.5rem;
        display: flex;
        flex-direction: column;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0.5rem 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 1.5rem;
        border-top: 0.0625rem solid ${colours.grey.G200};
    }
`;

const ScrollContainer = styledTag('div')`
    overflow-y: auto;
`;

const GalleryRow = styledComponent(Box)`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 0.25rem;
    cursor: pointer;
`;

StyledDialog.defaultProps = {
    maxWidth: 'xs',
    fullWidth: true,
};

interface Props extends ModalProps {
    adId?: string;
    postId?: string;
    newsPostId?: string;
    companyId?: string;
    userId?: string;
}

export const AddInGalleryModal: React.FC<Props> = ({ open, onClose, adId, postId, userId, companyId, newsPostId }) => {
    const { t } = useTranslation(['favorites', 'common']);
    const { addAlert } = useAlerts();
    const {
        addFavoritePostInCache,
        addFavoriteAdInCache,
        addFavoriteNewsPostInCache,
        incrementNumberOfContentsOnGalleryInCache,
    } = useApolloCacheUpdate();
    const { logEventDynamic } = useDynamicLogEvents();

    const {
        items: galleries,
        totalCount,
        loading,
        fetchMore,
    } = useGalleries({
        userId,
        companyId,
        policy: GalleryAccessPolicy.EditContent,
        limit: 10,
        skip: !open,
        fetchPolicy: 'network-only',
        includeCollaborators: true,
    });

    const [addContentToGallery] = useAddContentToGalleryMutation({
        onCompleted(data) {
            if (postId) {
                addFavoritePostInCache(postId);
            }
            if (adId) {
                addFavoriteAdInCache(adId);
            }
            if (newsPostId) {
                addFavoriteNewsPostInCache(newsPostId);
            }
            incrementNumberOfContentsOnGalleryInCache({
                id: data.addContentToGallery.id,
                numberOfContents: data.addContentToGallery.numberOfContents,
            });
        },
    });

    const handleSelectClick = async ({ id, name }: { id: string; name: string }) => {
        try {
            await addContentToGallery({
                variables: {
                    adId,
                    postId,
                    newsPostId,
                    galleryId: id,
                },
                refetchQueries: ['CurrentUser', ...galleriesQueries, 'GalleryContent', 'Gallery'],
                awaitRefetchQueries: true,
            });
            if (adId || postId || newsPostId) {
                logEventDynamic(AnalyticsEvent.AddToFavoritesConfirmation);
            }
            onClose();
            addAlert({
                type: 'success',
                message: t('favorites:addedInGalleryName', { galleryName: name }),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    const [manageGalleryModalOpen, setManageGalleryOpen] = useState(false);

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        if (bottom && (galleries?.length ?? 0) < (totalCount ?? 0) && !loading) {
            await fetchMore();
        }
    };
    return (
        <>
            <StyledDialog open={open} onClose={onClose}>
                <DialogTitle onClose={onClose}>
                    <Typography data-target-id="gallery-modal-title" variant="h2" style={{ color: colours.black.main }}>
                        {t('favorites:addToGallery')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {!loading && totalCount === 0 && (
                        <Box height="100%" display="flex" justifyContent="center" alignItems="center">
                            <NoFavoritesEmptyState text={t('favorites:addInGalleryModalEmptyStateText')} />
                        </Box>
                    )}
                    {totalCount && totalCount > 0 ? (
                        <Box my="1rem">
                            <Typography data-target-id="gallery-modal-count" variant="h4">
                                {t('favorites:galleriesCount', {
                                    count: totalCount,
                                    plurial: totalCount > 1 ? 's' : '',
                                })}
                            </Typography>
                        </Box>
                    ) : null}
                    <ScrollContainer onScroll={handleScroll}>
                        {galleries?.map((gallery) => (
                            <GalleryRow
                                key={gallery.id}
                                data-target="gallery-row"
                                data-target-id={`pgallery-row-id-${gallery.id}`}
                                onClick={() => handleSelectClick({ id: gallery.id, name: gallery.name })}
                            >
                                <GalleryPreviewRow
                                    gallery={gallery}
                                    showGalleryDataInfos={false}
                                    redirectOnClick={false}
                                    showSelectButtonOnHover
                                    changeBackgroundOnHover
                                />
                            </GalleryRow>
                        ))}
                        {loading && <Loader />}
                    </ScrollContainer>
                </DialogContent>

                <DialogActions>
                    <Button
                        data-target="gallery-modal-buttons"
                        data-target-id="gallery-modal-new-gallery"
                        onClick={() => {
                            onClose();
                            setManageGalleryOpen(true);
                        }}
                        variant="outlined"
                        startIcon={<Icon name="Plus" />}
                    >
                        {t('favorites:newGallery')}
                    </Button>
                </DialogActions>
            </StyledDialog>
            {manageGalleryModalOpen && (
                <ManageGalleryModal
                    open={manageGalleryModalOpen}
                    onClose={() => setManageGalleryOpen(false)}
                    postIdToAddInGalleryAfterCreate={postId}
                    newsPostIdToAddInGalleryAfterCreate={newsPostId}
                    adIdToAddInGalleryAfterCreate={adId}
                    companyId={companyId}
                    showCollaboratorsSelection
                />
            )}
        </>
    );
};
